<template>

  <div class="suggested-post" :style="{ marginBottom: `${gap}px` }">
    <!-- Two-column layout with bio underneath: User Info | Follow Button -->

    <div class="suggested-post__layout flex items-start gap-1">
      <!-- Left: Avatar + User Info + Bio -->
      <div class="suggested-post__left suggested-post__grid flex-1 min-w-0">
        <!-- Avatar: linked to the profile in normal mode, unlinked (org row has its own click target) in org mode -->
        <div class="suggested-post__avatar-cell flex-shrink-0 pr-1">
          <BaseAvatar v-if="!isOrg" :storage-id="authorData.storageId" :verified="authorData.verified" :href="userRoute"
            :isAdmin="authorData.isAdmin && isAdmin" :rating="authorData.rating"
            :isCurrentUser="userdata?.id === authorData.userId" :target="newTab ? '_blank' : '_self'" />
          <BaseAvatar v-else :storage-id="authorData.storageId" :verified="authorData.verified"
            :isAdmin="authorData.isAdmin && isAdmin" :rating="authorData.rating"
            :isCurrentUser="userdata?.id === authorData.userId" />
        </div>

        <!-- Tier badge directly under avatar, horizontally centered to avatar -->
        <!-- <div v-if="authorData.rating" class="suggested-post__badge-cell">
          <div class="suggested-post__badge-aligner">
            <ReputationBadge :score="authorData.rating" :showTierName="true"
              :isCurrentUser="userdata?.id === authorData.userId" :tierSet="tierSet" size="micro"
              class="suggested-post__tier-badge-avatar" />
          </div>
        </div> -->

        <!-- Follow/Unfollow under badge (shown only on tight widths, via CSS container queries — see .suggested-post__button-cell) -->
        <div class="suggested-post__button-cell">
          <BaseButton v-if="isOrg" class="base-btn--small !max-w-[78px] !min-w-[78px]" mode="secondary"
            @click="addUserToOrg">
            Add
          </BaseButton>
          <ButtonFollow v-if="isAuth && data.id !== userdata.id" :loading="isFollowLoading" :isFollow="isFollowing"
            @click="handleFollow(authorData.userId)" class="suggested-post__follow-button"
            :class="{ 'suggested-post__unfollow-button': isFollowing }" />
        </div>

        <!-- Name and username line (to the right of avatar); org and non-org branches render identically today but are kept separate for future divergence -->
        <div class="suggested-post__name-cell suggested-post__name-line flex items-center gap-2 flex-wrap">
          <div v-if="!isOrg"
            class="suggested-post__author-name flex items-center gap-1 hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleAuthorClick" :title="fullName">
            <span class="truncate">{{ fullName }}</span>
            <img v-if="badgeIconSrc" :src="badgeIconSrc" alt="badge" loading="lazy" decoding="async" class="w-[20px] h-[20px] object-contain flex-shrink-0" />
          </div>
          <div v-else class="suggested-post__author-name flex items-center gap-1 hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleAuthorClick"  :title="fullName">
            <span class="truncate">{{ fullName }}</span>
            <img v-if="badgeIconSrc" :src="badgeIconSrc" alt="badge" loading="lazy" decoding="async" class="w-[20px] h-[20px] object-contain flex-shrink-0" />
          </div>

          <!-- Username on same line -->
          <div v-if="authorData.username && !isOrg"
            class="suggested-post__username text-[0.8rem] text-[var(--color-text-02)] hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleAuthorClick">
            @{{ authorData.username }}
          </div>
          <div v-else-if="authorData.username && isOrg"
            class="suggested-post__username text-[0.8rem] text-[var(--color-text-02)] hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleAuthorClick">
            @{{ authorData.username }}
          </div>
        </div>

        <!-- Bio directly to the right of the badge (second row) -->
        <!-- <div class="suggested-post__bio-cell suggested-post__bio-section mt-0">
          <span v-if="authorData.bio" class="bio-text text-text02 cutLines text-xs text-left"
            v-html="sanitizeBioWithLinks(authorData.bio)"></span>
          <span v-else class="bio-text text-text02 text-xs italic opacity-60">
            No bio available
          </span>
        </div> -->
      </div>

      <!-- Right: Follow button (default wide-layout position; hidden in favor of .suggested-post__button-cell on narrow containers) -->
      <div class="suggested-post__right flex-shrink-0" style="width: 74px;">
        <BaseButton v-if="isOrg" class="base-btn--small !max-w-[78px] !min-w-[78px]" mode="secondary"
          @click="addUserToOrg">
          Add
        </BaseButton>
        <ButtonFollow v-if="isAuth && data.id !== userdata.id" :loading="isFollowLoading" :isFollow="isFollowing"
          @click="handleFollow(authorData.userId)" class="suggested-post__follow-button"
          :class="{ 'suggested-post__unfollow-button': isFollowing }" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { useToast } from "primevue/usetoast";
import { useStore } from "vuex";

import { authErrorHandler } from "@/services/errorServices/authHandler.js";
import { useFollowNotification } from "@/helpers/notificationHelpers/notificationMessages";
import ReputationBadge from "@/components/ReputationBadge.vue";
import { useTierSet } from "@/helpers/reputationTiersHelper.js";

import ButtonFollow from "../../components/ui-components/NewButtonFollow/index.vue";
import getFullName from "../../helpers/userHelpers/userGetFullName";
import { makeLegacyDecoration } from "@/helpers/urlDecorationHelper.js";
import { BaseURL } from "@/global/baseUrls.js";

const router = useRouter();

const notify = useFollowNotification();
const toast = useToast();
const store = useStore();
const userdata = computed(() => store.getters.getUser);
const isAdmin = computed(
  () => userdata.value?.roles?.includes("admin") || false
);

// Tier set for reputation badges
const { tierSet } = useTierSet();

/** @emits adduser - Fired when the "Add" button is clicked in org mode (org-add logic lives in the parent; this component just requests it). */
const emit = defineEmits(["adduser"]);

/**
 * @prop {Object} data - The suggested user or post record to render (shape depends on `type`).
 * @prop {'user'|'post'} type - Whether `data` is a user record or a post record; determines how `authorData` is derived.
 * @prop {Boolean} isAuth - Whether the viewer is authenticated; controls whether the Follow button is shown.
 * @prop {Boolean} isOrg - Whether this card represents an org suggestion; shows an "Add" button instead of Follow.
 * @prop {String|Number} gap - Bottom margin (px) applied to the card, for spacing between stacked suggestions.
 * @prop {Function} onCloseDropdown - Optional callback invoked after navigating away via a name/username click, to close a containing dropdown.
 * @prop {Boolean} newTab - Whether clicking the author's name opens their profile in a new tab instead of navigating in place.
 */
const props = defineProps({
  data: {
    type: Object,
    default: null
  },
  type: {
    type: String,
    default: "user"
  },
  isAuth: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  gap: {
    type: [String, Number],
    default: 15,
  },
  onCloseDropdown: {
    type: Function,
    default: null
  },
  newTab: {
    type: Boolean,
    default: true,
  },
});

/**
 * Normalize incoming data so templates can safely access fields.
 * Provide a minimal typed-like shape with known keys.
 */
const authorData = computed(() => {
  // Normalize incoming data so templates can safely access fields
  let obj = {
    storageId: undefined,
    verified: undefined,
    userId: undefined,
    roles: undefined,
    isAdmin: undefined,
    username: undefined,
    rating: undefined,
    firstName: undefined,
    lastName: undefined,
    bio: undefined,
  };

  if (props.type === "post") {
    const post = props.data || {};
    obj = { ...obj, ...(post || {}) };
    obj.storageId = post.avatarFile?.storageId;
    obj.verified = post.verified;
    obj.userId = post.id;
    obj.roles = post.authorUser?.roles || [];
    obj.isAdmin = Array.isArray(obj.roles) ? obj.roles.includes("admin") : false;
  } else if (props.type === "user") {
    const user = props.data || {};
    obj = { ...obj, ...(user || {}) };
    obj.storageId = user?.avatarFile?.storageId;
    obj.userId = user.id;
    obj.isAdmin = user.roles?.includes("admin") || false;
    obj.username = user.username; // Ensure username is copied

  }

  return obj;
});

// Refresh the current user's profile from the server
const refreshProfile = async () => {
  try {
    await store.dispatch('getMe');
  } catch (e) {
    console.error('Failed to refresh profile after follow/unfollow', e);
  }
};

/** Navigates to the author's profile page, in a new tab if `props.newTab` is set, otherwise in place. */
const handleUserClick = () => {
  const route = (!props.data?.username
    ? `/profile/${props.data?.id}`
    : `/user/${props.data?.username}`) + makeLegacyDecoration();

  if (props.newTab) {
    window.open(route, '_blank', 'noopener,noreferrer');
  } else {
    router.push(route);
  }
};
/** Handles clicking the author's name/username: navigates via `handleUserClick`, then closes any containing dropdown. */
const handleAuthorClick = () => {
  handleUserClick();
  props.onCloseDropdown?.();
};
/** Whether a follow/unfollow request is currently in flight. */
const isFollowLoading = ref(false);

/** Profile route for the author (by username if known, else by id), with the legacy URL decoration suffix appended. */
const userRoute = computed(() => {
  const route = (!props.data?.username
    ? `/profile/${props.data?.id}`
    : `/user/${props.data?.username}`) + makeLegacyDecoration();

  return route;
});

/** Whether the logged-in user already follows this card's author; null if `following` data isn't loaded yet. */
const isFollowing = computed(() => {
  if (!Array.isArray(userdata.value?.following)) return null;
  return userdata.value.following.includes(authorData.value.userId);
});
// const fullName = computed(() => {
//   let firstName = authorData.value.firstName;
//   let lastName = authorData.value.lastName;
//   let maxWidth = 9;

//   if (!firstName && !lastName) {
//     return authorData.value.username;
//   }

//   if (firstName) {
//     if (firstName.length > maxWidth) {
//       firstName = firstName.slice(0, maxWidth) + "..";
//     }
//   } else {
//     firstName = "";
//   }

//   if (lastName) {
//     if (lastName.length > maxWidth) {
//       lastName = lastName.slice(0, maxWidth) + "..";
//     }
//   } else {
//     lastName = "";
//   }

//   if (firstName && lastName) {
//     return `${firstName} ${lastName}`;
//   } else {
//     return firstName || lastName;
//   }
// });

/** Display name: "First Last" if both are known (untruncated — CSS handles overflow), otherwise whichever of first/last/username is available. */
const fullName = computed(() => {
  let firstName = authorData.value.firstName;
  let lastName = authorData.value.lastName;

  if (!firstName && !lastName) {
    return authorData.value.username;
  }

  // Return full name without truncation - CSS will handle overflow
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else {
    return firstName || lastName || "";
  }
});

/** Maps known badge keys to their CDN icon URLs. */
const BADGE_ICON_MAP = {
  verified: "https://cdn.digicollect.com/cdn/launchpad/verified.png",
  heart: "https://cdn.digicollect.com/cdn/launchpad/heart.png",
  flame: "https://cdn.digicollect.com/cdn/launchpad/flame.png",
  star: "https://cdn.digicollect.com/cdn/launchpad/star.png",
  crown: "https://cdn.digicollect.com/cdn/launchpad/crown.png",
  sparkles: "https://cdn.digicollect.com/cdn/launchpad/sparkles.png",
};
/** Resolves the badge image: a known key maps to a CDN icon, an unknown key is treated as a relative path against `BaseURL`, no key means no badge. */
const badgeIconSrc = computed(() => { const k = authorData.value?.userBadgeIcon ?? ""; return BADGE_ICON_MAP[k] || (k ? `${BaseURL}${k}` : ""); });

/** Emits `adduser` for the parent to handle adding this user to the org (used in `isOrg` mode instead of Follow). */
const addUserToOrg = () => {
  emit("adduser");
};

/**
 * Follows or unfollows the given user via the API, showing toasts for
 * blocked/conflict/error responses and updating the local `following` list
 * (then re-syncing the full profile in the background) on success.
 * @param {string} id - User id to follow/unfollow
 */
const handleFollow = async (id) => {
  isFollowLoading.value = true;
  if (!isFollowing.value) {
    try {
      const res = await fetch(`/api/users/${id}/follow`, {
        method: "POST",
      });

      const data = await res.json();
      if (data.statusCode === 403 && data.message === "User blocked you") {
        toast.add({
          severity: "warn",
          summary: "Why can't you do this?",
          detail:
            "This user has blocked you, so you can't perform this action.",
          life: 7000,
        });
        return;
      }
      if (
        (res.status === 401 || res.status === 403) &&
        String(data.message) !== "User blocked you"
      ) {
        authErrorHandler(res);
        return;
      }
      if (data.statusCode === 409) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: data.message,
          life: 7000,
        });
        return;
      }

      if (data.error) {
        const followerName = getFullName(props.data);
        notify("error", "followed", followerName);
      } else {
        const followerName = getFullName(props.data);
        notify("success", "followed", followerName);
        userdata.value.following.push(id);
        // Sync the full profile in the background — don't make the toast wait on it
        refreshProfile();
      }
    } catch (error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 7000,
      });
    } finally {
      isFollowLoading.value = false;
    }
  } else {
    try {
      const res = await fetch(`/api/users/${id}/unfollow`, {
        method: "POST",
      });
      if (res.status === 401 || res.status === 403) {
        authErrorHandler(res);
        return;
      }
      const data = await res.json();
      if (data.statusCode === 409) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: data.message,
          life: 7000,
        });
        return;
      }
      if (data.error) {
        // Error already handled above
      } else {
        const followerName = getFullName(props.data);
        notify("success", "unfollowed", followerName);
        userdata.value.following = userdata.value.following.filter(
          (userId) => userId !== id
        );
        // Sync the full profile in the background — don't make the toast wait on it
        refreshProfile();
      }
    } catch (error) {
      store.commit("setUserDataError", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 7000,
      });
    } finally {
      isFollowLoading.value = false;
    }
  }
};

/**
 * Escapes HTML special characters in a value for safe inclusion in `v-html` content.
 * @param {*} value - Value to escape (coerced to a string)
 * @returns {string} HTML-escaped string
 */
const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

/**
 * Escapes a bio string and then wraps any bare URLs within it as clickable links.
 * (Currently unused — the bio section in the template is commented out.)
 * @param {string} bio - Raw bio text
 * @returns {string} HTML-escaped bio with URLs turned into `<a>` tags
 */
const sanitizeBioWithLinks = (bio) => {
  if (!bio) return '';
  const escapedBio = escapeHtml(bio);
  return escapedBio.replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="hover:underline">$1</a>'
  );
};
</script>

<style lang="scss" scoped>
.suggested-post {
  width: 100%;
  display: flex;
  align-items: flex-start;
  background: transparent;
  // box-shadow: none;
  border: 0;
  // border-radius: 5.8px;
  // border: 1px solid rgba(186, 186, 186, 0.50);
  /* OLD: border-bottom: 1px solid #727272; */
  border-bottom: 1px solid var(--color-text-muted);
  // background: var(--background-mode, rgba(19, 19, 22, 0.80));
  // box-shadow: 10px 10px 60px 0 rgba(0, 0, 0, 0.50);
  backdrop-filter: blur(50px);
  height: 62px;
  padding: 10px;
}

// Two-column layout for suggested for you section
.suggested-post__layout {
  width: 100%;
  // align-items: flex-start;
  align-items: center;
  // gap: 28px;
  /* Tweakable offset to pull the text block closer to the avatar */
  --name-left-pull: 0px;
  /* no left pull by default to avoid overlap */
  /* Allow wrapping at constrained widths to prevent overlap with follow button */
  flex-wrap: nowrap;
  // margin-bottom: 1rem;
}

.suggested-post__left {
  flex: 1 1 auto;
  min-width: 0;
  /* Reserve space for the right-side button and gaps to avoid overlap */
  max-width: calc(100% - 80px);
}

/* Grid layout: 2 columns (avatar/badge | text) and 2 rows (name | bio) */
.suggested-post__grid {
  display: flex;
  gap: 4px;
  // display: grid;
  // grid-template-columns: 88px 1fr;
  // /* allow space for widest badge label like 'Diamond' */
  // grid-template-rows: auto auto auto;
  // /* name | bio | under-badge button */
  // grid-column-gap: 8px;
  // grid-row-gap: 1px;
  // /* slightly tighter vertical spacing */
  // align-items: start;
}

.suggested-post__avatar-cell {
  grid-column: 1;
  grid-row: 1;
}

.suggested-post__badge-cell {
  grid-column: 1;
  grid-row: 2;
  margin-top: 2px;
  /* Ensure space for the widest tier label like 'Diamond' */
  min-width: 88px;
  /* match grid column width */
}

/* Follow/Unfollow button that can move under the badge on tight widths */
.suggested-post__button-cell {
  grid-column: 1;
  grid-row: 3;
  /* sits below the badge */
  display: none;
  /* hidden by default; shown at narrow widths */
  margin-top: 4px;
}

/* Center badge directly under the avatar */
.suggested-post__badge-aligner {
  /* avatar is 48px wide in this context; center badge within the avatar column */
  width: 48px;
  display: flex;
  justify-content: center;
}

.suggested-post__badge-aligner:hover .suggested-post__tier-badge-avatar,
.suggested-post__badge-aligner:focus-within .suggested-post__tier-badge-avatar {
  filter: drop-shadow(0 0 0.6px var(--color-surface-border, rgba(0, 0, 0, 0.6))) drop-shadow(0 0 4px rgba(59, 130, 246, 0.2));
}

.suggested-post__name-cell {
  grid-column: 2;
  grid-row: 1;
  /* Visually pull left toward avatar */
  margin-left: 0;
}

.suggested-post__bio-cell {
  grid-column: 2;
  grid-row: 2;
  /* Visually pull left toward avatar */
  margin-left: 0;
}

.suggested-post__bio-section {
  width: 100%;

  .bio-text {
    display: -webkit-box;
    -webkit-line-clamp: 1; // show just one line in compact mode
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.3;
    word-wrap: break-word;
    max-width: 100%;
    hyphens: auto;
  }
}

.suggested-post__right {
  flex-shrink: 0;
  width: 78px; // Fixed width for follow button
  display: flex;
  align-items: flex-start;
  justify-content: end;
}

.suggested-post__follow-button {
  margin-bottom: 0 !important;
  transition: 0.2s ease-in;
  font-size: 0.8rem;
  background-color: var(--color-accent-primary);
  /* Keep button within right-section width; prevent overflow */
  width: 70%;
  max-width: 78px;
  min-width: 70px;
  white-space: nowrap;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: var(--color-text-black-white);

  &:hover {
    background-color: var(--color-brand-secondary);
    color: var(--color-text-black-white);
  }
}

.suggested-post__unfollow-button {
  border: 1px solid var(--color-brand-secondary, #a3e635);
  background-color: transparent;
  /* OLD: color: var(--color-brand-secondary, #a3e635); */
  color: var(--color-brand-secondary, var(--color-brand-lime));

  &:hover {
    /* OLD: background-color: var(--color-brand-secondary, #a3e635); */
    background-color: var(--color-brand-secondary, var(--color-brand-lime));
    /* OLD: color: var(--color-button-on-primary, #000); */
    color: var(--color-button-on-primary, var(--color-bg-black));
  }
}

/* Dark mode: ensure readable text on lime background */
[scheme='dark'] .suggested-post__follow-button,
[scheme='dark'] .suggested-post__unfollow-button:hover {
  /* OLD: color: #000 !important; */
  color: var(--color-bg-black) !important;
}

/* Slightly smaller badge so it doesn't encroach into bio */
.suggested-post__tier-badge-avatar {
  transform: scale(1.1);
  /* ~10% larger for visibility */
  transform-origin: left top;
  display: inline-block;
  /* Thin outline for contrast over varied backgrounds */
  filter: drop-shadow(0 0 0.6px var(--color-surface-border, rgba(0, 0, 0, 0.45)));
  transition: filter .15s ease, transform .15s ease;
}

// Author info alignment
.suggested-post__author-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  font-size: 12px;
  line-height: 1.2;
  min-width: 0;
  width: 100%;
}

.suggested-post__name-line {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0px;
  flex-wrap: wrap;
  min-width: 0;
}

.suggested-post__author-name {
  font-weight: 600;
  // color: var(--color-text-primary-v2) !important;
  color: var(--color-quick-link-icon-bg) !important;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
}

.suggested-post__username {
  // color: var(--color-text-02);
  color: var(--color-text-02-v2);
  font-size: 0.8rem;
  flex-shrink: 0;
}

.suggested-post__tier-badge-container {
  margin-top: 0;
}

/* Compact avatar size for suggested block */
.suggested-post__avatar-compact :deep(img),
.suggested-post__avatar-compact :deep(.avatar),
.suggested-post__avatar-compact :deep(.base-avatar),
.suggested-post__avatar-compact :deep(svg) {
  width: 36px !important;
  height: 36px !important;
}

/* Inline badge spacing and scale */
.suggested-post__tier-badge-inline {
  display: inline-flex;
  align-items: center;
  transform: scale(1.32);
  /* ~10% larger inline badge */
  transform-origin: left center;
  margin-left: 2px;
  margin-top: 1px;
  filter: drop-shadow(0 0 0.6px var(--color-surface-border, rgba(0, 0, 0, 0.45)));
  transition: filter .15s ease;
}

.suggested-post__tier-badge-inline:hover,
.suggested-post__tier-badge-inline:focus {
  filter: drop-shadow(0 0 0.6px var(--color-surface-border, rgba(0, 0, 0, 0.6))) drop-shadow(0 0 4px rgba(59, 130, 246, 0.2));
}

/* Responsive safeguards to avoid overlap on narrow rightbar widths */
/* Container query: respond to the actual rightbar suggested block width */
@container suggested-block (max-width: 320px) {

  /* When the suggested block (container) is narrow, move the button under the badge */
  .suggested-post__layout {
    flex-wrap: wrap;
  }

  .suggested-post__left {
    width: 100%;
    max-width: 100%;
  }

  .suggested-post__button-cell {
    display: block;
  }

  .suggested-post__right {
    display: none;
  }
}

@media (max-width: 980px) {

  /* Tighten avatar grid so text has a bit more room */
  .suggested-post__grid {
    grid-template-columns: 72px 1fr;
  }

  .suggested-post__badge-cell {
    min-width: 72px;
  }
}

@container suggested-block (max-width: 280px) {

  /* Maintain under-badge button in extremely tight rightbar widths */
  .suggested-post__button-cell {
    display: block;
  }

  .suggested-post__right {
    display: none;
  }

  .suggested-post__follow-button {
    min-width: 78px;
    width: auto;
  }
}

/* Fallback for browsers without container queries */
@supports not (container-type: inline-size) {
  @media (max-width: 1120px) {
    .suggested-post__layout {
      flex-wrap: wrap;
    }

    .suggested-post__left {
      width: 100%;
      max-width: 100%;
    }

    .suggested-post__button-cell {
      display: block;
    }

    .suggested-post__right {
      display: none;
    }
  }
}
</style>
